import React, { useEffect, useRef, useState } from "react";
import * as faceapi from "face-api.js";
import axios from "axios";
import { useSelector } from "react-redux";
import useFaceCamera from "./useFaceCamera";
import { useLocation } from "react-router-dom";

export default function RegisterFace() {
  const { userData } = useSelector((state) => state.authentication);
  const location = useLocation(); // 👈 to trigger re-render on route change (reset state)
  const student = location.state?.student; // 👈 get student data from route state (if any)
  const employee = location.state?.employee; // 👈 get employee data from route state (if any)

  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  // ⚡ Safely evaluate state with optional chaining to prevent crashes
  const [name, setName] = useState(
    student
      ? [student.firstName, student.middleName, student.lastName].filter(Boolean).join(" ")
      : employee?.Full_Name || ""
  );

  const [employeeId, setEmployeeId] = useState(
    student?.studentId || employee?.EmployeeId || ""
  );

  const canvasRef = useRef(null);
  const prevDescriptorRef = useRef(null);
  
  console.log("Current Employee Data:", employee);

  const {
    videoRef,
    cameraOn,
    startCamera,
    stopCamera,
  } = useFaceCamera(modelsLoaded);

  /* ---------------- LOAD MODELS ---------------- */
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = "/models";

        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ]);

        setModelsLoaded(true);
        setStatus("Models loaded ✅");
      } catch (err) {
        console.error("MODEL LOAD ERROR:", err);
        setStatus(err?.message || "Model loading failed ❌");
      }
    };

    loadModels();
  }, []);

  /* ---------------- FACE QUALITY ---------------- */
  const isFaceGood = (detection, video) => {
    if (!detection) return false;

    const faceBox = detection?.detection?.box;
    if (!faceBox) {
      setStatus("🙂 Face not clear");
      return false;
    }

    /* ---------- FACE SIZE ---------- */
    if (faceBox.width < 140) {
      setStatus("📏 Move closer");
      return false;
    }

    /* ---------- CENTER ---------- */
    const centerX = faceBox.x + faceBox.width / 2;
    const frameCenter = video.videoWidth / 2;

    if (Math.abs(centerX - frameCenter) > 100) {
      setStatus("🎯 Center your face");
      return false;
    }

    /* ---------- SHARPNESS ---------- */
    const landmarks = detection.landmarks;
    if (!landmarks) {
      setStatus("🙂 Face blur");
      return false;
    }

    const leftEye = landmarks.getLeftEye();
    const rightEye = landmarks.getRightEye();

    if (!leftEye?.length || !rightEye?.length) {
      setStatus("🙂 Face blur");
      return false;
    }

    const dx = rightEye[0].x - leftEye[0].x;
    const dy = rightEye[0].y - leftEye[0].y;
    const eyeDistance = Math.sqrt(dx * dx + dy * dy);

    if (eyeDistance < 45) {
      setStatus("🙂 Face blur");
      return false;
    }

    return true;
  };

  /* ---------------- DETECTION ---------------- */
  const detectFace = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || video.readyState < 2) {
      return null;
    }

    const detection = await faceapi
      .detectSingleFace(video, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.25 }))
      .withFaceLandmarks(true)
      .withFaceDescriptor();

    /* ---------- DRAW ---------- */
    if (canvas && video) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (detection) {
        const dims = faceapi.matchDimensions(canvas, video, true);
        const resized = faceapi.resizeResults(detection, dims);
        const drawBox = new faceapi.draw.DrawBox(resized.detection.box, {
          label: "Face",
        });
        drawBox.draw(canvas);
      }
    }

    if (!detection) {
      setStatus("📷 Face not detected");
      return null;
    }

    if (!isFaceGood(detection, video)) {
      return null;
    }

    return detection;
  };

  /* ---------------- STABILITY CHECK ---------------- */
  const isStable = (descriptor) => {
    if (!prevDescriptorRef.current) {
      prevDescriptorRef.current = descriptor;
      return false;
    }

    const dist = faceapi.euclideanDistance(prevDescriptorRef.current, descriptor);
    prevDescriptorRef.current = descriptor;

    return dist < 0.22;
  };

  /* ---------------- MULTI CAPTURE ---------------- */
  const captureDescriptors = async (count = 4) => {
    const samples = [];
    let attempts = 0;
    const maxAttempts = 20;

    while (samples.length < count && attempts < maxAttempts) {
      attempts++;
      setStatus(`Capturing ${samples.length + 1}/${count}...`);

      await new Promise((r) => setTimeout(r, 500));
      const detection = await detectFace();

      if (!detection) {
        continue;
      }

      const descriptor = detection.descriptor;
      if (!isStable(descriptor)) {
        setStatus("🙂 Hold still...");
        continue;
      }

      samples.push(Array.from(descriptor));
      setStatus(`✅ Captured ${samples.length}/${count}`);
    }

    if (samples.length < 3) {
      throw new Error("Face capture failed");
    }

    return samples;
  };

  /* ---------------- REGISTER ---------------- */
  const handleRegister = async () => {
    if (!name || !employeeId) {
      setStatus("Enter name & ID registration credentials");
      return;
    }

    if (!cameraOn) {
      setStatus("Start camera first");
      return;
    }

    setLoading(true);
    setStatus("📷 Preparing face...");

    try {
      const descriptors = await captureDescriptors(4);
      setStatus("💾 Saving...");

      await axios.post(
        `${process.env.REACT_APP_COLLEGE_ERP_API}faces/${userData.branch}/register`,
        {
          name,
          employeeId,
          descriptors, // ⚡ save MULTIPLE descriptors
        },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        }
      );

      setStatus("✅ Registration successful");
      stopCamera();
    } catch (err) {
      console.error("FULL ERROR:", err);
      console.error("RESPONSE:", err?.response?.data);
      setStatus(
        err?.response?.data?.message || err.message || "❌ Registration failed"
      );
    }

    setLoading(false);
  };

  /* ---------------- UI ---------------- */
  return (
    <div style={{ maxWidth: 420, margin: "auto", textAlign: "center" }}>
      <h3>Face Registration</h3>

      <input
        placeholder={student ? "Student ID" : "Employee ID"}
        value={employeeId}
        onChange={(e) => setEmployeeId(e.target.value)}
        style={{ width: "100%", marginBottom: 10 }}
      />

      <input
        placeholder="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{ width: "100%", marginBottom: 10 }}
      />

      {!cameraOn && (
        <button onClick={startCamera} disabled={!modelsLoaded}>
          {modelsLoaded ? "Start Camera" : "Loading Models..."}
        </button>
      )}

      {cameraOn && (
        <>
          <div style={{ position: "relative", marginTop: 10 }}>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              width="100%"
              style={{ borderRadius: 12 }}
            />
            <canvas
              ref={canvasRef}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
            />
          </div>

          <button onClick={handleRegister} disabled={loading}>
            {loading ? "Processing..." : "Register Face"}
          </button>

          <button onClick={stopCamera}>Stop Camera</button>
        </>
      )}

      <p>{status}</p>
    </div>
  );
}